import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "./AuthContext";

import { BACKEND_URL } from "@/config/runtime";

const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error("useNotifications must be used within NotificationProvider");
  }
  return context;
};

// Notification sound
const playNotificationSound = () => {
  try {
    const audio = new Audio("/notification.mp3");
    audio.volume = 0.5;
    audio.play().catch(() => {});
  } catch (e) {
    // Could not play notification sound
  }
};

export const NotificationProvider = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pushSupported, setPushSupported] = useState(false);
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const lastCountRef = useRef(0);
  const pollIntervalRef = useRef(null);

  // Check if push notifications are supported
  useEffect(() => {
    if ("serviceWorker" in navigator && "PushManager" in window) {
      setPushSupported(true);
    }
  }, []);

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/notifications?limit=30`, {
        credentials: "include",
      });
      if (res.ok) {
        setNotifications(await res.json());
      }
    } catch (e) {
      console.error("Failed to fetch notifications:", e);
    }
  }, [isAuthenticated]);

  // Fetch unread count
  const fetchUnreadCount = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/notifications/unread-count`, {
        credentials: "include",
      });
      if (res.ok) {
        const data = await res.json();
        const newCount = data.count;
        
        // Play sound if count increased and sound is enabled
        if (newCount > lastCountRef.current && settings?.sound_enabled !== false) {
          playNotificationSound();
        }
        lastCountRef.current = newCount;
        setUnreadCount(newCount);
      }
    } catch (e) {
      console.error("Failed to fetch unread count:", e);
    }
  }, [isAuthenticated, settings?.sound_enabled]);

  // Fetch notification settings
  const fetchSettings = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/notifications/settings`, {
        credentials: "include",
      });
      if (res.ok) {
        setSettings(await res.json());
      }
    } catch (e) {
      console.error("Failed to fetch notification settings:", e);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  // Mark notification as read
  const markAsRead = async (notificationId) => {
    try {
      await fetch(`${BACKEND_URL}/api/notifications/${notificationId}/read`, {
        method: "PUT",
        credentials: "include",
      });
      setNotifications((prev) =>
        prev.map((n) =>
          n.notification_id === notificationId ? { ...n, is_read: true } : n
        )
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (e) {
      console.error("Failed to mark notification as read:", e);
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    try {
      await fetch(`${BACKEND_URL}/api/notifications/read-all`, {
        method: "PUT",
        credentials: "include",
      });
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (e) {
      console.error("Failed to mark all as read:", e);
    }
  };

  // Delete notification
  const deleteNotification = async (notificationId) => {
    try {
      await fetch(`${BACKEND_URL}/api/notifications/${notificationId}`, {
        method: "DELETE",
        credentials: "include",
      });
      setNotifications((prev) =>
        prev.filter((n) => n.notification_id !== notificationId)
      );
    } catch (e) {
      console.error("Failed to delete notification:", e);
    }
  };

  // Delete all notifications
  const deleteAllNotifications = async () => {
    try {
      await fetch(`${BACKEND_URL}/api/notifications/all`, {
        method: "DELETE",
        credentials: "include",
      });
      setNotifications([]);
      setUnreadCount(0);
    } catch (e) {
      console.error("Failed to delete all notifications:", e);
    }
  };

  // Update settings
  const updateSettings = async (newSettings) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/notifications/settings`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(newSettings),
      });
      if (res.ok) {
        setSettings((prev) => ({ ...prev, ...newSettings }));
        return true;
      }
    } catch (e) {
      console.error("Failed to update settings:", e);
    }
    return false;
  };

  // Update team-specific settings
  const updateTeamSettings = async (teamId, teamSettings) => {
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/notifications/settings/team/${teamId}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(teamSettings),
        }
      );
      if (res.ok) {
        setSettings((prev) => ({
          ...prev,
          team_overrides: {
            ...prev?.team_overrides,
            [teamId]: teamSettings,
          },
        }));
        return true;
      }
    } catch (e) {
      console.error("Failed to update team settings:", e);
    }
    return false;
  };

  // Subscribe to push notifications
  const subscribeToPush = async () => {
    if (!pushSupported) return false;
    
    try {
      // Request permission
      const permission = await Notification.requestPermission();
      if (permission !== "granted") return false;

      // Get VAPID public key
      const vapidRes = await fetch(`${BACKEND_URL}/api/notifications/push/vapid-key`);
      const { publicKey } = await vapidRes.json();
      
      if (!publicKey) {
        console.error("VAPID public key not available");
        return false;
      }

      // Register service worker
      const registration = await navigator.serviceWorker.register("/sw.js");
      await navigator.serviceWorker.ready;

      // Subscribe
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });

      // Send subscription to server
      const res = await fetch(`${BACKEND_URL}/api/notifications/push/subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          endpoint: subscription.endpoint,
          keys: {
            p256dh: arrayBufferToBase64(subscription.getKey("p256dh")),
            auth: arrayBufferToBase64(subscription.getKey("auth")),
          },
        }),
      });

      if (res.ok) {
        setPushSubscribed(true);
        return true;
      }
    } catch (e) {
      console.error("Failed to subscribe to push:", e);
    }
    return false;
  };

  // Unsubscribe from push
  const unsubscribeFromPush = async () => {
    try {
      const registration = await navigator.serviceWorker.getRegistration();
      const subscription = await registration?.pushManager.getSubscription();
      
      if (subscription) {
        await subscription.unsubscribe();
      }

      await fetch(`${BACKEND_URL}/api/notifications/push/unsubscribe`, {
        method: "DELETE",
        credentials: "include",
      });
      
      setPushSubscribed(false);
      return true;
    } catch (e) {
      console.error("Failed to unsubscribe from push:", e);
    }
    return false;
  };

  // Check push subscription status
  const checkPushSubscription = async () => {
    if (!pushSupported) return;
    try {
      const registration = await navigator.serviceWorker.getRegistration();
      const subscription = await registration?.pushManager.getSubscription();
      setPushSubscribed(!!subscription);
    } catch (e) {
      console.error("Failed to check push subscription:", e);
    }
  };

  // Initial fetch
  useEffect(() => {
    if (isAuthenticated) {
      fetchNotifications();
      fetchUnreadCount();
      fetchSettings();
      checkPushSubscription();
    } else {
      setNotifications([]);
      setUnreadCount(0);
      setSettings(null);
      setLoading(false);
    }
  }, [isAuthenticated, fetchNotifications, fetchUnreadCount, fetchSettings]);

  // Poll for new notifications
  useEffect(() => {
    if (isAuthenticated) {
      pollIntervalRef.current = setInterval(() => {
        fetchUnreadCount();
      }, 30000); // Poll every 30 seconds
    }
    
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [isAuthenticated, fetchUnreadCount]);

  const value = {
    notifications,
    unreadCount,
    settings,
    loading,
    pushSupported,
    pushSubscribed,
    fetchNotifications,
    fetchUnreadCount,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    deleteAllNotifications,
    updateSettings,
    updateTeamSettings,
    subscribeToPush,
    unsubscribeFromPush,
    refetch: () => {
      fetchNotifications();
      fetchUnreadCount();
    },
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

// Utility functions
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

function arrayBufferToBase64(buffer) {
  let binary = "";
  const bytes = new Uint8Array(buffer);
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
}

export default NotificationProvider;
